import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import OTPInput from 'react-otp-input';
// import { ToastContainer, toast } from 'react-toastify';
import toast from 'react-hot-toast';
//import 'react-toastify/dist/ReactToastify.css';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import API from '../services/api';
import AuthLayout from '../component/ui/AuthLayout';
import { inputClass } from '../component/ui/Field';
import Button from '../component/ui/Button';


const Signup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    address1: '',
    address2: '',
    city: '',
    country: 'India',
    gstNumber: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingCountry: '',
    isShippingSameAsBilling: false, // state to toggle shipping address same as billing
  });

  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[6-9]\d{9}$/;
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!strongPasswordRegex.test(password)) {
      toast.error("Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character.", {
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'dark',
      });
      return;
    }
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
  
    if (!mobileRegex.test(mobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
  
    if (password !== confirmPassword) {
      toast.error("Passwords do not match", {
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
      return;
    }
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/register`, { name, mobile, email, password, businessInfo });
      setOtpSent(true);
    } catch (error) {
      console.error('Signup failed', error);
      toast.error(error.response.data.message, {
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/verify-otp`, { email, otp });
      setIsVerified(true);
      navigate('/login');
    } catch (error) {
      console.error('OTP verification failed', error);
      toast.error("Wrong OTP", {
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (otpValue) => {
    setOtp(otpValue);
  };

  const handleCheckboxChange = () => {
    setBusinessInfo((prevState) => ({
      ...prevState,
      isShippingSameAsBilling: !prevState.isShippingSameAsBilling,
      shippingAddress1: prevState.isShippingSameAsBilling ? '' : prevState.address1,
      shippingAddress2: prevState.isShippingSameAsBilling ? '' : prevState.address2,
      shippingCity: prevState.isShippingSameAsBilling ? '' : prevState.city,
      shippingCountry: prevState.isShippingSameAsBilling ? '' : prevState.country,
    }));
  };

  if (otpSent && !isVerified) {
    return (
      <AuthLayout>
        <h1 className="text-xl font-semibold text-slate-900">Verify your email</h1>
        <p className="text-sm text-slate-500 mt-1">An OTP has been sent to <strong className="text-slate-700">{email}</strong></p>
        <form onSubmit={handleOtpSubmit} className="mt-6">
          <OTPInput
            value={otp}
            onChange={handleOTPChange}
            numInputs={6}
            renderInput={(props) => (
              <input
                {...props}
                placeholder="-"
                className="w-12 h-12 border border-slate-300 bg-slate-50 rounded-md text-center text-lg focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
              />
            )}
            containerStyle={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
            }}
          />
          <Button type="submit" variant="accent" size="lg" className="w-full mt-6" disabled={loading}>
            {loading ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : "Verify"}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout formWidth="max-w-3xl">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
          <UserPlus size={18} />
        </span>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Create your account</h1>
          <p className="text-xs text-slate-500">Set up your business to start creating quotations</p>
        </div>
      </div>

      <form onSubmit={handleSignupSubmit} className="mt-6 space-y-6">
        {/* Account */}
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
            <input
              type="text"
              placeholder="Mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className={inputClass}
              required
            />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} sm:col-span-2`}
              required
            />
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} pr-9`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass}
              required
            />
          </div>
        </section>

        {/* Business info */}
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Business information</h2>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Business name"
              value={businessInfo.businessName}
              onChange={(e) => setBusinessInfo({ ...businessInfo, businessName: e.target.value })}
              className={inputClass}
              required
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Address line 1"
                value={businessInfo.address1}
                onChange={(e) => setBusinessInfo({ ...businessInfo, address1: e.target.value })}
                className={inputClass}
                required
              />
              <input
                type="text"
                placeholder="Address line 2"
                value={businessInfo.address2}
                onChange={(e) => setBusinessInfo({ ...businessInfo, address2: e.target.value })}
                className={inputClass}
              />
              <input
                type="text"
                placeholder="City"
                value={businessInfo.city}
                onChange={(e) => setBusinessInfo({ ...businessInfo, city: e.target.value })}
                className={inputClass}
                required
              />
              <input
                type="text"
                placeholder="Country"
                value={businessInfo.country}
                onChange={(e) => setBusinessInfo({ ...businessInfo, country: e.target.value })}
                className={inputClass}
                required
              />
            </div>
            <input
              type="text"
              placeholder="GST number"
              value={businessInfo.gstNumber}
              onChange={(e) => setBusinessInfo({ ...businessInfo, gstNumber: e.target.value })}
              className={inputClass}
            />

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={businessInfo.isShippingSameAsBilling}
                onChange={handleCheckboxChange}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600/40"
              />
              Shipping address same as billing address
            </label>

            {!businessInfo.isShippingSameAsBilling && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Shipping address line 1"
                  value={businessInfo.shippingAddress1}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, shippingAddress1: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Shipping address line 2"
                  value={businessInfo.shippingAddress2}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, shippingAddress2: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Shipping city"
                  value={businessInfo.shippingCity}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, shippingCity: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Shipping country"
                  value={businessInfo.shippingCountry}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, shippingCountry: e.target.value })}
                  className={inputClass}
                />
              </div>
            )}
          </div>
        </section>

        <Button type="submit" variant="accent" size="lg" className="w-full" disabled={loading}>
          {loading ? (
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          ) : 'Create account'}
        </Button>

        <p className="text-center text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-700 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default Signup;
